import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {any | null} */
  #productJson = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache the JSON that product-price uses (so we can update instantly without waiting fetch+morph)
    this.#productJson = this.#readProductJsonSafe();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    // IMPORTANT: input/click fires instantly; change can be delayed
    this.addEventListener('input', this.variantChanged);
    this.addEventListener('click', this.variantChanged);
    this.addEventListener('change', this.variantChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.variantChanged);
    this.removeEventListener('click', this.variantChanged);
    this.removeEventListener('change', this.variantChanged);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Only react to actual option inputs/selects
    const isOptionInput =
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLSelectElement ||
      event.target.matches?.('input, select');

    if (!isOptionInput) return;

    // Some clicks happen on labels/spans — climb to the input if needed
    let target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) {
      const maybeInput = target.closest('label')?.querySelector('input') || target.closest('fieldset')?.querySelector('input');
      if (maybeInput instanceof HTMLInputElement) target = maybeInput;
    }

    const selectedOption =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;

    if (!selectedOption) return;

    // 1) Update selection visuals immediately
    this.updateSelectedOption(target);

    // 2) Dispatch selected event (theme logic)
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // 3) INSTANT price update (no waiting for fetch+morph)
    this.#instantUpdatePriceFromCurrentState();

    // 4) Continue normal theme flow (fetch updated HTML + morph)
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    // 5) Update URL variant param
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  };

  /**
   * Updates the selected option UI state.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) radios[currentIndex].dataset.previousChecked = 'false';
          if (previousIndex !== undefined && radios[previousIndex]) radios[previousIndex].dataset.previousChecked = 'false';

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty('--pill-width-current', `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`);
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty('--pill-width-previous', `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`);
          }
        }
      }

      // Force checked right away
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;

        // Always morph UI even if JSON is missing
        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Update our cached JSON for future instant updates
          if (textContent) {
            try {
              this.#productJson = JSON.parse(textContent);
            } catch (_) {}
          }

          // Dispatch ONE VariantUpdateEvent (this is what product-price listens for)
          if (textContent && this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        // After morph, run instant update again (covers cases where price container got replaced)
        this.#instantUpdatePriceFromCurrentState();
      })
      .catch((error) => {
        if (error.name === 'AbortError') console.warn('Fetch aborted by user');
        else console.error(error);
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }

  // ----------------------------
  // Instant price update helpers
  // ----------------------------

  #readProductJsonSafe() {
    try {
      const text = this.querySelector(`script[type="application/json"]`)?.textContent;
      if (!text) return null;
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  #formatMoneyFromCents(cents) {
    // Use Shopify.formatMoney if exists (best)
    if (window?.Shopify?.formatMoney) {
      // Theme usually has a money format in Shopify.money_format
      const fmt = window.Shopify?.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    // Fallback
    return `$${(cents / 100).toFixed(2)}`;
  }

  #instantUpdatePriceFromCurrentState() {
    // Scope to the same section/dialog (avoids updating wrong product price on pages with multiple products)
    const scope = this.closest('.shopify-section, dialog') || document;

    const priceContainer = scope.querySelector('product-price [ref="priceContainer"]');
    if (!priceContainer) return;

    // If theme actually outputs data-variant-price, use it
    const checked = this.querySelector('fieldset input:checked');
    const selectedFromSelect = this.querySelector('select option[selected]');
    const el = checked || selectedFromSelect;
    if (!el) return;

    const directPrice = el.dataset.variantPrice;
    if (directPrice) {
      priceContainer.innerHTML = directPrice;
      return;
    }

    // Otherwise: use variantId + cached product JSON
    const variantId = Number(el.dataset.variantId || 0);
    if (!variantId || !this.#productJson) return;

    const variants = this.#productJson?.product?.variants || this.#productJson?.variants || [];
    const v = variants.find((x) => Number(x.id) === variantId);
    if (!v) return;

    // Many themes want full HTML, but updating text fast is enough (morph will replace later anyway)
    // We set the main price text wherever possible.
    const money = this.#formatMoneyFromCents(v.price);
    // Try common patterns
    const moneyEl =
      priceContainer.querySelector('[data-money]') ||
      priceContainer.querySelector('.price__regular .price-item') ||
      priceContainer.querySelector('.price-item') ||
      priceContainer;

    if (moneyEl) moneyEl.textContent = money;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

/* --------------------------
   OnlyYou helper (optional)
--------------------------- */

function onlyyouUpdateVariantHelp(root = document) {
  const sizeDescMap = {
    Small: 'Perfect for a thoughtful gesture',
    Medium: 'Our most popular size',
    Large: 'Make a grand impression',
    Lavish: 'The ultimate statement piece',
  };

  const vaseAddonMap = {
    Small: 25,
    Medium: 35,
    Large: 45,
    Lavish: 55,
  };

  const sizeDescEl = root.querySelector('#onlyyouSizeDesc');
  const vaseDescEl = root.querySelector('#onlyyouVaseDesc');
  if (!sizeDescEl || !vaseDescEl) return;

  const getSelected = (name) => {
    const radio = root.querySelector(`input[name="options[${name}]"]:checked`);
    if (radio) return radio.value;

    const select = root.querySelector(`select[name="options[${name}]"]`);
    if (select) return select.value;

    const fieldsets = Array.from(root.querySelectorAll('fieldset'));
    for (const fs of fieldsets) {
      const legendText = fs.querySelector('legend')?.textContent?.trim();
      if (legendText && legendText.toLowerCase() === name.toLowerCase()) {
        const checked = fs.querySelector('input:checked');
        if (checked) return checked.value;
      }
    }

    return null;
  };

  const size = getSelected('Size');
  const presentation = getSelected('Presentation');

  sizeDescEl.textContent = size ? `${size} — ${sizeDescMap[size] || ''}` : '';

  const isVase = presentation && presentation.toLowerCase().includes('vase');
  if (isVase && size && vaseAddonMap[size] != null) {
    vaseDescEl.style.display = 'block';
    vaseDescEl.textContent = `Includes premium glass vase (+$${vaseAddonMap[size]})`;
  } else {
    vaseDescEl.style.display = 'none';
    vaseDescEl.textContent = '';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  onlyyouUpdateVariantHelp(document);

  // Fast updates: input/click/change inside variant-picker
  ['input', 'click', 'change'].forEach((evt) => {
    document.addEventListener(evt, (e) => {
      if (e.target && e.target.closest && e.target.closest('variant-picker')) {
        requestAnimationFrame(() => onlyyouUpdateVariantHelp(document));
      }
    });
  });
});

// After morph/update
document.addEventListener('variant:update', () => onlyyouUpdateVariantHelp(document));
window.addEventListener('popstate', () => onlyyouUpdateVariantHelp(document));
